import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth, hasPermission } from '../contexts/AuthContext';
import BarcodeManager from './BarcodeManager';
import { NAV_SECTIONS } from './navConfig';
import { Barcode, X, PanelLeftClose, PanelLeftOpen, Warehouse } from 'lucide-react';
import logo from '../assets/logo-stacked.png';

const Sidebar = ({ isOpen, onClose, collapsed, onToggleCollapse }) => {
  const { user } = useAuth();
  const location = useLocation();
  const [showBarcodeManager, setShowBarcodeManager] = useState(false);
  const role = user?.role;
  const isAdmin = hasPermission(role, 'admin');

  const visible = (item) => {
    if (item.exactRole === 'admin' && !isAdmin) return false;
    if (item.hideForAdmin && isAdmin) return false;
    if (item.minRole && !hasPermission(role, item.minRole)) return false;
    return true;
  };

  return (
    <>
      <aside
        className={`sidebar ${isOpen ? 'open' : ''} ${collapsed ? 'w-[68px]' : 'w-64'}`}
        data-testid="sidebar"
      >
        {/* Brand */}
        <div className="flex items-center gap-2.5 h-14 px-3 border-b border-slate-200 flex-shrink-0">
          <img src={logo} alt="SND" className="h-8 w-8 object-contain flex-shrink-0" />
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-slate-900 leading-tight truncate">StarAndDaisy</p>
              <p className="eyebrow leading-tight">Inventory Ops</p>
            </div>
          )}
          <button
            onClick={onClose}
            className="lg:hidden btn-icon ml-auto"
            data-testid="sidebar-close-btn"
            aria-label="Close menu"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden py-3">
          {NAV_SECTIONS.map((section) => {
            if (section.minRole && !hasPermission(role, section.minRole)) return null;
            const items = section.items.filter(visible);
            if (items.length === 0) return null;

            return (
              <div key={section.title} className="mb-4 last:mb-0">
                {!collapsed && (
                  <div className="px-4 mb-1.5">
                    <span className="eyebrow">{section.title}</span>
                  </div>
                )}
                {collapsed && <div className="mx-4 mb-2 section-rule" />}
                <div className="space-y-0.5">
                  {items.map((item) => {
                    const active = location.pathname === item.to;
                    return (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        onClick={onClose}
                        title={collapsed ? item.label : undefined}
                        className={`sidebar-nav-item ${active ? 'active' : ''} ${collapsed ? 'justify-center px-0' : ''}`}
                        data-testid={`nav-${item.to.replace(/\//g, '-').replace(/^-/, '')}`}
                      >
                        <item.icon className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={active ? 2.3 : 1.9} />
                        {!collapsed && <span className="truncate">{item.label}</span>}
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            );
          })}

          <div className="mt-2 pt-3 border-t border-slate-200 mx-2">
            <button
              onClick={() => setShowBarcodeManager(true)}
              title={collapsed ? 'Print Barcodes' : undefined}
              className={`sidebar-nav-item w-[calc(100%-1rem)] mx-0 ${collapsed ? 'justify-center px-0' : ''}`}
              data-testid="print-barcodes-btn"
            >
              <Barcode className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={1.9} />
              {!collapsed && <span>Print Barcodes</span>}
            </button>
          </div>
        </nav>

        {/* Scope + collapse */}
        <div className="flex-shrink-0 border-t border-slate-200 p-2">
          {!collapsed && (
            <div className="flex items-center gap-2 px-2 py-2 rounded-lg bg-surface-2 border border-slate-200 mb-2">
              <Warehouse className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <div className="min-w-0">
                <p className="eyebrow leading-tight">Scope</p>
                <p className="text-xs font-semibold text-slate-800 truncate" data-testid="sidebar-scope">
                  {user?.warehouse?.name || 'All Warehouses'}
                </p>
              </div>
            </div>
          )}
          <button
            onClick={onToggleCollapse}
            className={`hidden lg:flex btn-ghost w-full text-xs ${collapsed ? 'justify-center px-0' : 'justify-start'}`}
            data-testid="sidebar-collapse-btn"
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
            {!collapsed && <span>Collapse</span>}
          </button>
        </div>
      </aside>

      <BarcodeManager isOpen={showBarcodeManager} onClose={() => setShowBarcodeManager(false)} />
    </>
  );
};

export default Sidebar;
